import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import { ExamQuestionPreview } from "@/features/exam-management";
import type { ExamPreviewQuestion } from "@/features/exam-management";
import { formatDateLong } from "@/utils";
import { clientExamsApi } from "../../services/client-exams.api";
import type {
  ClientExamQuestionsQuestionDto,
  ClientExamResultData,
} from "../../types/client-exams.types";
import styles from "./ExamResultDrawer.module.scss";

function mapResultQuestionsToPreview(
  questions: ClientExamQuestionsQuestionDto[]
): ExamPreviewQuestion[] {
  return [...questions]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((q) => ({
      id: q.id,
      text: q.questionText ?? "",
      options: (q.options ?? []).map((o) => ({
        id: o.id,
        text: o.optionText ?? "",
        isCorrect: undefined,
      })),
      allowMultiple: q.allowMultipleSelection ?? false,
      imagePreview: q.imageUrl ?? q.image?.downloadUrl ?? undefined,
    }));
}

function buildAnswersByQuestionIndex(
  questions: ClientExamQuestionsQuestionDto[]
): Record<number, string[]> {
  const sorted = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const out: Record<number, string[]> = {};
  sorted.forEach((q, index) => {
    const ids = q.selectedOptionIds ?? [];
    if (ids.length) out[index] = ids;
  });
  return out;
}

function buildSubmittedByQuestionIndex(
  questions: ClientExamQuestionsQuestionDto[],
  answers: Record<number, string[]>
): Record<number, boolean> {
  const sorted = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const out: Record<number, boolean> = {};
  sorted.forEach((q, index) => {
    if ((q.selectedOptionIds?.length ?? 0) > 0 || (answers[index]?.length ?? 0) > 0) {
      out[index] = true;
    }
  });
  return out;
}

interface ExamResultDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  educationId: string | null;
  attemptId: string | null;
  examName?: string;
}

const ExamResultDrawer: React.FC<ExamResultDrawerProps> = ({
  isOpen,
  onClose,
  educationId,
  attemptId,
  examName,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClientExamResultData | null>(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);

  useEffect(() => {
    if (!isOpen || !educationId || !attemptId) {
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setSelectedQuestionIndex(0);
    clientExamsApi
      .getExamResult(educationId, attemptId)
      .then((data) => setResult(data))
      .catch(() =>
        setError(t("clientExams.resultDrawer.loadError"))
      )
      .finally(() => setLoading(false));
  }, [isOpen, educationId, attemptId, t]);

  const previewQuestions = useMemo(
    () => (result?.questions ? mapResultQuestionsToPreview(result.questions) : []),
    [result?.questions]
  );

  const answersByQuestionIndex = useMemo(() => {
    if (!result?.questions) return {};
    return buildAnswersByQuestionIndex(result.questions);
  }, [result?.questions]);

  const submittedByQuestionIndex = useMemo(() => {
    if (!result?.questions) return {};
    return buildSubmittedByQuestionIndex(result.questions, answersByQuestionIndex);
  }, [result?.questions, answersByQuestionIndex]);

  const handlePrev = useCallback(() => {
    setSelectedQuestionIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleNext = useCallback(() => {
    setSelectedQuestionIndex((i) =>
      Math.min(Math.max(0, previewQuestions.length - 1), i + 1)
    );
  }, [previewQuestions.length]);

  const attempt = result?.attempt;
  const passingScore = result?.passingScore ?? 0;
  const scorePct = attempt?.scorePercentage ?? 0;
  const passed =
    typeof scorePct === "number" && typeof passingScore === "number"
      ? scorePct >= passingScore
      : undefined;

  const title = examName ?? t("clientExams.resultDrawer.title");

  const timerDisplay = { hours: "00", minutes: "00", seconds: "00" };

  const selectedOptionIds = answersByQuestionIndex[selectedQuestionIndex] ?? [];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      width="min(960px, 96vw)"
      position="right"
    >
      <div className={styles.root}>
        {loading && (
          <div className={styles.stateMessage}>
            {t("common.loading")}
          </div>
        )}
        {error && !loading && <div className={styles.errorMessage}>{error}</div>}
        {!loading && !error && result && attempt && (
          <>
            <div className={styles.summary}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>
                  {t("clientExams.resultDrawer.score")}
                </span>
                <span
                  className={`${styles.summaryValue} ${
                    passed === true ? styles.pass : passed === false ? styles.fail : ""
                  }`}
                >
                  %{Math.round(scorePct)}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>
                  {t("clientExams.resultDrawer.passingScore")}
                </span>
                <span className={styles.summaryValue}>%{Math.round(passingScore)}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>
                  {t("clientExams.resultDrawer.outcome")}
                </span>
                <span
                  className={`${styles.summaryValue} ${
                    passed === true ? styles.pass : passed === false ? styles.fail : ""
                  }`}
                >
                  {passed === true
                    ? t("clientExams.resultDrawer.passed")
                    : passed === false
                      ? t("clientExams.resultDrawer.failed")
                      : "—"}
                </span>
              </div>
              {attempt.finishedAt && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>
                    {t("clientExams.resultDrawer.finishedAt")}
                  </span>
                  <span className={styles.summaryValue}>
                    {formatDateLong(attempt.finishedAt) || attempt.finishedAt}
                  </span>
                </div>
              )}
            </div>
            {previewQuestions.length > 0 && (
              <div className={styles.previewWrap}>
                <ExamQuestionPreview
                  questions={previewQuestions}
                  selectedQuestionIndex={selectedQuestionIndex}
                  onSelectQuestion={setSelectedQuestionIndex}
                  onPrev={handlePrev}
                  onNext={handleNext}
                  timerDisplay={timerDisplay}
                  mode="take"
                  selectedOptionIds={selectedOptionIds}
                  answersByQuestionIndex={answersByQuestionIndex}
                  submittedByQuestionIndex={submittedByQuestionIndex}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ExamResultDrawer;
